var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/** @file This file contains the submission model definition. */
import Big from 'big.js';
import { Maybe } from 'purify-ts/Maybe';
import * as api from '@codegrade/api';
import { assert, datetime, object, typeHelpers } from '@codegrade/utils';
import { NONEXISTENT } from '@/constants';
import { AssignmentsStore, FeedbackStore, FileTreeStore, SiteSettingsStore, store } from '@/store';
import * as mutationTypes from '@/store/mutation-types';
import { CacheDecorator } from '@/utils/cache';
/**
 * Add a user to the store.
 *
 * @param user The user to add to the store.
 */
function commitUser(user) {
    store.commit(`users/${mutationTypes.ADD_OR_UPDATE_USER}`, user);
}
/** The data contained in the submission class. */
class SubmissionData {
    /** @param props The data to store in the submission class. */
    constructor(props) {
        Object.assign(this, props);
    }
    /** The id of the submission. */
    id;
    /** The moment the submission was created. */
    createdAt;
    /** The id of the assignment the submission belongs to. */
    assignmentId;
    /** The id of the user (might be a group) that created the submission. */
    userId;
    /** How the submission was created. */
    origin;
    /** The grade as returned from the API (so unscaled) but wrapped in a Maybe. */
    maybeGrade;
    /** Has the rubric grade been overridden. */
    gradeOverridden;
    /** The id of the user that should grade this submission. */
    assigneeId;
    /** Any extra info about the submission. */
    extraInfo;
    /** The timeframe used to hand-in this submission. */
    timeframe;
}
/** The class that represents a submission by a user or group of users. */
export class Submission extends SubmissionData {
    /** The cache used for expensive getters. */
    #cache = new CacheDecorator();
    /** The public cache of the submission. */
    get _cache() {
        return this.#cache;
    }
    /** @param props The data to construct the submission with. */
    constructor(props) {
        super(props);
        Object.freeze(this);
    }
    /**
     * Construct a Submission from the raw API data.
     *
     * @param serverData The data we got from the server, without any parsing.
     * @returns A fresh `Submission` object, it might throw an error if we can't parse the data.
     */
    static fromServerData(serverData) {
        const apiServerData = api.ExtendedWork.decode(serverData)
            .mapLeft(errValue => new Error(errValue))
            .unsafeCoerce();
        return Submission.fromApiServerData(apiServerData);
    }
    /**
     * Create a new submission object from the data returned by the API library.
     *
     * @param data The data we received from the API after parsing by the API package.
     * @returns A fresh submission object.
     */
    static fromApiServerData(data) {
        commitUser(data.user);
        data.assignee.ifJust(commitUser);
        data.commentAuthor.ifJust(commitUser);
        data.rubricResult.ifJust(rubricResult => {
            store.commit(`rubrics/${mutationTypes.SET_RUBRIC_RESULT}`, {
                assignmentId: data.assignmentId,
                submissionId: data.id,
                result: rubricResult,
            });
        });
        return new Submission({
            id: data.id,
            assignmentId: data.assignmentId,
            origin: data.origin,
            extraInfo: data.extraInfo,
            gradeOverridden: data.gradeOverridden,
            userId: data.user.id,
            assigneeId: data.assignee.map(object.attr('id')).extractNullable(),
            createdAt: data.createdAt,
            maybeGrade: data.grade.map(g => new Big(g)),
            timeframe: data.timeframe,
        });
    }
    /** @returns The time we should stop trying to load the ATv2 results because they will not appear anymore. */
    get retryATv2ResultUntil() {
        const extraTime = SiteSettingsStore.getSetting()('NEW_AUTO_TEST_OLD_SUBMISSION_AGE', this.maybeAssignment.chain(assig => assig.maybeTenantId));
        return this.createdAt.clone().add(extraTime);
    }
    /**
     * Update this submission returning a fresh submission.
     *
     * @param newProps The props to update.
     * @returns A fresh submission with the updated props.
     */
    update(newProps) {
        const data = { ...this };
        for (const key of typeHelpers.keysOfSlow(['grade', 'assignee'])) {
            if (newProps[key] === undefined) {
                continue;
            }
            switch (key) {
                case 'grade':
                    data.maybeGrade = Maybe.fromNullable(newProps.grade).map(g => new Big(g));
                    break;
                case 'assignee': {
                    const assignee = api.User.unsafeDecode(newProps.assignee);
                    commitUser(assignee);
                    data.assigneeId = assignee.id;
                    break;
                }
                default:
                    throw assert.AssertionError.assertNever(key);
            }
        }
        return new Submission(data);
    }
    /** @returns The moment the submission was created (with year). */
    get formattedCreatedAt() {
        return datetime.readableFormatDate(this.createdAt);
    }
    /**
     * The moment the submission was created, in the shortest format.
     *
     * @returns The created at of this assignment, in a format that is easily readable for the
     * user.
     */
    get readableCreatedAt() {
        return datetime.formatMoment(this.createdAt, 'MMM DD, HH:mm');
    }
    /**
     * Getter for the formatted grade.
     *
     * @returns The grade of a submission as a formatted string.
     * This is ready for display and is scaled.
     */
    get formattedGrade() {
        return this.maybeGrade.map(grade => this.maybeAssignment.caseOf({
            Just: assig => assig.formatUnscaledGrade(grade),
            Nothing: () => grade.toFixed(2),
        }));
    }
    /** @returns The file tree for this assignment, or `undefined` if we don't have any loaded. */
    get fileTree() {
        return FileTreeStore.getFileTree()(this.assignmentId, this.id).extract();
    }
    /** @returns All the feedback for this assignment, or `undefined` if we don't have any. */
    get feedback() {
        return FeedbackStore.getFeedback()(this.assignmentId, this.id);
    }
    /** @returns Maybe the general feedback for this assignment. */
    get generalFeedback() {
        return Maybe.fromNullable(this.feedback).chain(object.attr('general'));
    }
    /**
     * Get the feedback thread for a given line and file.
     *
     * @param fileId The id of the file to get the feedback for.
     * @param line The line to get the feedback for.
     * @returns Maybe the thread for the given file and line combination, might be `Nothing` because
     * the thread doesn't exist, or because we haven't loaded that data yet.
     */
    feedbackForLine(fileId, line) {
        return Maybe.fromNullable(this.feedback)
            .chainNullable(object.attr('user'))
            .chainNullable(object.attr(fileId))
            .chainNullable(object.attr(line));
    }
    /**
     * Maybe the rubric result for this submission.
     *
     * @returns The rubric result for this submission, will be `Nothing` if we haven't loaded the
     * data yet, will be `Just(Nothing)` if we did load the data but there is not rubric result.
     */
    get rubricResult() {
        const result = store.getters['rubrics/results'][this.id];
        return Maybe.fromNullable(result).map(res => Maybe.fromPredicate(x => x !== NONEXISTENT, res));
    }
    /** @returns The assignment connected to the submission as a Maybe. */
    get maybeAssignment() {
        return AssignmentsStore.getAssignment()(this.assignmentId);
    }
    /** @returns The assignment connected to the submission. Undefined if not found. */
    get assignment() {
        return this.maybeAssignment.extract();
    }
    /** @returns The user that is the author of this submission, might be a group. */
    get user() {
        return store.getters['users/getUser'](this.userId);
    }
    /** @returns Maybe the user that is assigned to grade this submission. */
    get assignee() {
        return Maybe.fromNullable(this.assigneeId).map(store.getters['users/getUser']);
    }
    /**
     * Check if the `createAt` of this submission is after a certain time.
     *
     * @param time The time to check, if `Nothing` the `createdAt` is never after the time.
     * @returns If the submission was created after the given time.
     */
    _isAfter(time) {
        return time.caseOf({
            Just: t => this.createdAt.isAfter(t),
            Nothing: () => false,
        });
    }
    /** @returns Whether the submission is late. */
    get isLate() {
        const res = this._isAfter(this.timeframe.deadline) || this._isAfter(this.timeframe.lockDate);
        // If we are late a late reason should be known.
        if (!IS_PRODUCTION) {
            assert.AssertionError.assert(this.lateReason.isJust() === res);
        }
        return res;
    }
    /** @returns Whether the submission is late. */
    get lateReason() {
        const checkLate = this.createdAt.isAfter.bind(this.createdAt);
        const maybeDeadline = this.timeframe.deadline.filter(checkLate);
        const maybeLockdate = this.timeframe.lockDate.filter(checkLate);
        return maybeDeadline.caseOf({
            Just: deadline => maybeLockdate.caseOf({
                Just: (lockDate) => Maybe.of(Object.freeze({
                    tag: 'both',
                    deadline,
                    lockDate,
                })),
                Nothing: () => Maybe.of(Object.freeze({ tag: 'deadline', deadline })),
            }),
            Nothing: () => maybeLockdate.map(lockDate => Object.freeze({
                tag: 'lock-date',
                lockDate,
            })),
        });
    }
}
__decorate([
    CacheDecorator.cache
], Submission.prototype, "formattedCreatedAt", null);
__decorate([
    CacheDecorator.cache
], Submission.prototype, "readableCreatedAt", null);
__decorate([
    CacheDecorator.cache
], Submission.prototype, "isLate", null);
__decorate([
    CacheDecorator.cache
], Submission.prototype, "lateReason", null);
